/**
 * Admin Settings - Improved tabbed layout with proper API integration
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Settings, Save, Loader2, Globe, Bell, Shield, 
  DollarSign, Mail, Image, Check, RefreshCw, Server,
  Eye, EyeOff, Key, Database, Zap, AlertTriangle, Clock,
  CalendarClock
} from 'lucide-react';
import axios from '../../lib/axios';
import { toast } from 'sonner';
import { API, useAuth } from '../../App';

const SettingRow = ({ label, description, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4 border-b border-border last:border-0">
    <div className="flex-1">
      <p className="font-medium text-sm">{label}</p>
      {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
    </div>
    <div className="sm:w-64">{children}</div>
  </div>
);

const Toggle = ({ enabled, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`relative w-11 h-6 rounded-full transition-colors ${
      enabled ? 'bg-primary' : 'bg-secondary border border-border'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
      enabled ? 'translate-x-5' : 'translate-x-0.5'
    }`} />
  </button>
);

const AdminSettings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // General
    site_name: 'Island Marketplace',
    site_description: 'Buy and sell in the Caribbean',
    maintenance_mode: false,
    // Listings
    max_images_per_listing: 10,
    auto_approve_listings: true,
    listing_expiry_days: 90,
    featured_listing_price: 5.00,
    // Users
    require_email_verification: true,
    allow_business_accounts: true,
    max_listings_per_user: 50,
    // Moderation
    auto_moderate_images: true,
    profanity_filter: true,
    require_review_verification: false,
    // Notifications
    email_notifications: true,
    admin_email: '',
    // Exchange Rate
    exchange_rate_auto_update: true,
    // Social Media
    instagram_url: '',
    facebook_url: '',
  });
  
  // Boost settings (separate state for dedicated management)
  const [boostSettings, setBoostSettings] = useState({
    enabled: false,
    packages: [
      { id: '24h', name: '24 Uur Boost', description: 'Zet je advertentie 24 uur bovenaan', duration_hours: 24, price: 1.75, price_cents: 175, currency: 'XCG' },
      { id: '7d', name: '7 Dagen Boost', description: 'Zet je advertentie 7 dagen bovenaan', duration_hours: 168, price: 9.00, price_cents: 900, currency: 'XCG' },
      { id: '30d', name: '1 Maand Boost', description: 'Zet je advertentie 30 dagen bovenaan', duration_hours: 720, price: 18.00, price_cents: 1800, currency: 'XCG' }
    ],
    currency: 'XCG'
  });
  const [boostLoading, setBoostLoading] = useState(true);
  const [boostSaving, setBoostSaving] = useState(false);
  const [sentooConfigured, setSentooConfigured] = useState(false);

  // Coming Soon settings
  const [comingSoon, setComingSoon] = useState({
    enabled: false,
    countdown_date: '',
    title: 'Coming Soon',
    subtitle: "We're working hard to bring you something amazing. Stay tuned!"
  });
  const [comingSoonSaving, setComingSoonSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/admin/settings`);
      // Merge fetched settings with defaults
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // Settings endpoint might not exist yet, use defaults
      console.log('Using default settings');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchBoostSettings = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/admin/boost/settings`);
      if (response.data) {
        const { settings: boostData, is_sentoo_configured, default_packages } = response.data;
        // Use saved settings if available, otherwise use defaults
        const packages = boostData?.packages || default_packages || boostSettings.packages;
        setBoostSettings(prev => ({
          ...prev,
          enabled: boostData?.enabled || false,
          packages: packages,
          currency: boostData?.currency || 'EUR'
        }));
        setSentooConfigured(is_sentoo_configured || false);
      }
    } catch (error) {
      console.log('Using default boost settings');
    } finally {
      setBoostLoading(false);
    }
  }, [user]);

  // Fetch Coming Soon settings
  const fetchComingSoon = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/admin/coming-soon`);
      if (response.data) {
        setComingSoon({
          enabled: response.data.enabled || false,
          countdown_date: response.data.countdown_date || '',
          title: response.data.title || 'Coming Soon',
          subtitle: response.data.subtitle || "We're working hard to bring you something amazing. Stay tuned!"
        });
      }
    } catch (error) {
      console.log('Using default coming soon settings');
    }
  }, [user]);

  useEffect(() => {
    fetchSettings();
    fetchBoostSettings();
    fetchComingSoon();
  }, [fetchSettings, fetchBoostSettings, fetchComingSoon]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const updateBoostPackage = (packageId, field, value) => {
    setBoostSettings(prev => ({
      ...prev,
      packages: prev.packages.map(pkg => {
        if (pkg.id === packageId) {
          const updated = { ...pkg, [field]: value };
          // Sync price_cents when price changes
          if (field === 'price') {
            updated.price_cents = Math.round(parseFloat(value || 0) * 100);
          }
          return updated;
        }
        return pkg;
      })
    }));
  };

  const handleSaveBoostSettings = async () => {
    setBoostSaving(true);
    try {
      await axios.put(`${API}/admin/boost/settings`, {
        enabled: boostSettings.enabled,
        packages: boostSettings.packages,
        currency: boostSettings.currency
      });
      toast.success('Boost instellingen opgeslagen');
    } catch (error) {
      toast.error('Kon boost instellingen niet opslaan');
    } finally {
      setBoostSaving(false);
    }
  };

  const handleToggleBoost = async () => {
    const newEnabled = !boostSettings.enabled;
    setBoostSettings(prev => ({ ...prev, enabled: newEnabled }));
    
    try {
      await axios.post(`${API}/admin/boost/toggle`);
      toast.success(newEnabled ? 'Boost geactiveerd' : 'Boost gedeactiveerd');
    } catch (error) {
      // Revert on error
      setBoostSettings(prev => ({ ...prev, enabled: !newEnabled }));
      toast.error('Kon boost status niet wijzigen');
    }
  };

  // Coming Soon save handler
  const handleSaveComingSoon = async () => {
    setComingSoonSaving(true);
    try {
      await axios.put(`${API}/admin/coming-soon`, comingSoon);
      toast.success('Coming Soon instellingen opgeslagen');
    } catch (error) {
      toast.error('Kon Coming Soon instellingen niet opslaan');
    } finally {
      setComingSoonSaving(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'coming-soon', label: 'Coming Soon', icon: CalendarClock },
    { id: 'listings', label: 'Listings', icon: Image },
    { id: 'boost', label: 'Boost', icon: Zap },
    { id: 'users', label: 'Users', icon: Shield },
    { id: 'moderation', label: 'Moderation', icon: Eye },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'social', label: 'Social Media', icon: Globe },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Settings</h2>
          <p className="text-sm text-muted-foreground">Manage your marketplace configuration</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-secondary/50 rounded-lg overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id 
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-card border border-border rounded-xl p-6">
        {activeTab === 'general' && (
          <div className="space-y-0">
            <SettingRow label="Site Name" description="The name of your marketplace">
              <input
                type="text"
                value={settings.site_name}
                onChange={(e) => updateSetting('site_name', e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
              />
            </SettingRow>
            <SettingRow label="Site Description" description="Brief description for SEO">
              <input
                type="text"
                value={settings.site_description}
                onChange={(e) => updateSetting('site_description', e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
              />
            </SettingRow>
            <SettingRow label="Maintenance Mode" description="Temporarily disable the marketplace">
              <Toggle
                enabled={settings.maintenance_mode}
                onChange={(v) => updateSetting('maintenance_mode', v)}
              />
            </SettingRow>
            <SettingRow label="Auto Update Exchange Rate" description="Automatically fetch latest USD/XCG rate">
              <Toggle
                enabled={settings.exchange_rate_auto_update}
                onChange={(v) => updateSetting('exchange_rate_auto_update', v)}
              />
            </SettingRow>
          </div>
        )}

        {activeTab === 'coming-soon' && (
          <div className="space-y-6">
            <div className="flex items-start justify-between pb-4 border-b border-border">
              <div>
                <h3 className="font-semibold">Coming Soon Modus</h3>
                <p className="text-sm text-muted-foreground">
                  Toon een "Coming Soon" pagina op de homepage. Andere pagina's blijven toegankelijk via directe URL.
                </p>
              </div>
              <Toggle
                enabled={comingSoon.enabled}
                onChange={(v) => setComingSoon(prev => ({ ...prev, enabled: v }))}
              />
            </div>
            
            {comingSoon.enabled && (
              <div className="space-y-4 p-4 bg-secondary/50 rounded-xl">
                <div>
                  <label className="block text-sm font-medium mb-2">Countdown Einddatum & Tijd</label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Voer de datum en tijd in zoals je wilt dat deze wordt weergegeven (AST/lokale tijd Curaçao).
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Datum</label>
                      <input
                        type="date"
                        value={comingSoon.countdown_date ? comingSoon.countdown_date.split('T')[0] : ''}
                        onChange={(e) => {
                          const currentTime = comingSoon.countdown_date ? comingSoon.countdown_date.split('T')[1]?.slice(0, 5) || '18:00' : '18:00';
                          setComingSoon(prev => ({ 
                            ...prev, 
                            countdown_date: `${e.target.value}T${currentTime}:00`
                          }));
                        }}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Tijd (AST)</label>
                      <input
                        type="time"
                        value={comingSoon.countdown_date ? comingSoon.countdown_date.split('T')[1]?.slice(0, 5) || '18:00' : '18:00'}
                        onChange={(e) => {
                          const currentDate = comingSoon.countdown_date ? comingSoon.countdown_date.split('T')[0] : new Date().toISOString().split('T')[0];
                          setComingSoon(prev => ({ 
                            ...prev, 
                            countdown_date: `${currentDate}T${e.target.value}:00`
                          }));
                        }}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  {comingSoon.countdown_date && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Ingesteld op: {new Date(comingSoon.countdown_date).toLocaleDateString('nl-NL', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Titel</label>
                  <input
                    type="text"
                    value={comingSoon.title}
                    onChange={(e) => setComingSoon(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Coming Soon"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Ondertitel</label>
                  <textarea
                    value={comingSoon.subtitle}
                    onChange={(e) => setComingSoon(prev => ({ ...prev, subtitle: e.target.value }))}
                    placeholder="We're working hard to bring you something amazing."
                    rows={2}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm resize-none"
                  />
                </div>
              </div>
            )}
            
            <div className="flex justify-end pt-4">
              <button
                onClick={handleSaveComingSoon}
                disabled={comingSoonSaving}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {comingSoonSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Opslaan
              </button>
            </div>
            
            {comingSoon.enabled && (
              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-500">Coming Soon Actief</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Bezoekers van de homepage zien nu de Coming Soon pagina. 
                      Jij kunt nog steeds toegang krijgen tot /admin, /auth, /marketplace, etc.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'listings' && (
          <div className="space-y-0">
            <SettingRow label="Max Images Per Listing" description="Maximum number of images allowed">
              <input
                type="number"
                value={settings.max_images_per_listing}
                onChange={(e) => updateSetting('max_images_per_listing', parseInt(e.target.value) || 10)}
                min="1"
                max="20"
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
              />
            </SettingRow>
            <SettingRow label="Auto Approve Listings" description="Automatically approve new listings">
              <Toggle
                enabled={settings.auto_approve_listings}
                onChange={(v) => updateSetting('auto_approve_listings', v)}
              />
            </SettingRow>
            <SettingRow label="Listing Expiry Days" description="Days until listings expire (0 = never)">
              <input
                type="number"
                value={settings.listing_expiry_days}
                onChange={(e) => updateSetting('listing_expiry_days', parseInt(e.target.value) || 90)}
                min="0"
                max="365"
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
              />
            </SettingRow>
            <SettingRow label="AI Screenshot Import" description="Enable AI-powered import from screenshots (uses credits)">
              <Toggle
                enabled={settings.ai_screenshot_import_enabled !== false}
                onChange={(v) => updateSetting('ai_screenshot_import_enabled', v)}
              />
            </SettingRow>
          </div>
        )}

        {activeTab === 'boost' && (
          <div className="space-y-6">
            {/* Sentoo Status Indicator */}
            <div className={`flex items-center gap-3 p-4 rounded-xl border ${
              sentooConfigured 
                ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' 
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            }`}>
              <div className={`w-3 h-3 rounded-full ${sentooConfigured ? 'bg-emerald-500' : 'bg-red-500'} animate-pulse`} />
              <div className="flex-1">
                <p className={`font-medium text-sm ${sentooConfigured ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                  {sentooConfigured ? 'Sentoo Geconfigureerd' : 'Sentoo Niet Geconfigureerd'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {sentooConfigured 
                    ? 'API credentials zijn geladen. Betalingen kunnen worden verwerkt.' 
                    : 'SENTOO_MERCHANT_ID en SENTOO_SECRET niet gevonden in .env'}
                </p>
              </div>
              {sentooConfigured && (
                <span className="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 text-xs font-medium rounded-full">
                  Live
                </span>
              )}
            </div>

            {/* Warning Banner when enabled but not configured */}
            {boostSettings.enabled && !sentooConfigured && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm text-amber-700 dark:text-amber-400">
                    Let op: Boost is actief maar Sentoo is niet geconfigureerd
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
                    Gebruikers zien de Boost UI, maar betalingen zullen falen. 
                    Voeg SENTOO_MERCHANT_ID en SENTOO_SECRET toe aan je .env bestand.
                  </p>
                </div>
              </div>
            )}

            {/* Enable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">Boost Feature Inschakelen</p>
                  <p className="text-xs text-muted-foreground">
                    Verwijdert de "Coming Soon" overlay en activeert betalingen
                  </p>
                </div>
              </div>
              <Toggle
                enabled={boostSettings.enabled}
                onChange={handleToggleBoost}
              />
            </div>

            {/* Package Editor */}
            <div>
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Boost Pakketten
              </h3>
              <div className="space-y-3">
                {boostSettings.packages.map((pkg) => (
                  <div 
                    key={pkg.id}
                    className="p-4 bg-secondary/50 border border-border rounded-xl"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-center">
                      {/* Package Name */}
                      <div className="sm:col-span-4">
                        <label className="text-xs text-muted-foreground mb-1 block">Naam</label>
                        <input
                          type="text"
                          value={pkg.name}
                          onChange={(e) => updateBoostPackage(pkg.id, 'name', e.target.value)}
                          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                        />
                      </div>
                      
                      {/* Duration */}
                      <div className="sm:col-span-3">
                        <label className="text-xs text-muted-foreground mb-1 block flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Duur (uren)
                        </label>
                        <input
                          type="number"
                          value={pkg.duration_hours}
                          onChange={(e) => updateBoostPackage(pkg.id, 'duration_hours', parseInt(e.target.value) || 24)}
                          min="1"
                          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                        />
                      </div>
                      
                      {/* Price */}
                      <div className="sm:col-span-3">
                        <label className="text-xs text-muted-foreground mb-1 block">Prijs (XCG)</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs font-medium">XCG</span>
                          <input
                            type="number"
                            value={pkg.price}
                            onChange={(e) => updateBoostPackage(pkg.id, 'price', parseFloat(e.target.value) || 0)}
                            min="0"
                            step="0.01"
                            className="w-full pl-12 pr-3 py-2 bg-background border border-border rounded-lg text-sm"
                          />
                        </div>
                      </div>
                      
                      {/* Preview */}
                      <div className="sm:col-span-2 text-right">
                        <span className="text-xs text-muted-foreground block mb-1">Preview</span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-medium rounded-full">
                          <Zap className="w-3 h-3" />
                          {pkg.duration_hours < 48 ? `${pkg.duration_hours}h` : `${Math.round(pkg.duration_hours / 24)}d`}
                        </span>
                      </div>
                    </div>
                    
                    {/* Description */}
                    <div className="mt-3">
                      <label className="text-xs text-muted-foreground mb-1 block">Beschrijving</label>
                      <input
                        type="text"
                        value={pkg.description}
                        onChange={(e) => updateBoostPackage(pkg.id, 'description', e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                        placeholder="Beschrijving voor gebruikers..."
                      />
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Save Boost Settings Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSaveBoostSettings}
                  disabled={boostSaving}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium hover:from-amber-600 hover:to-orange-600 transition-colors disabled:opacity-50"
                >
                  {boostSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Boost Instellingen Opslaan
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-0">
            <SettingRow label="Require Email Verification" description="Users must verify email to post">
              <Toggle
                enabled={settings.require_email_verification}
                onChange={(v) => updateSetting('require_email_verification', v)}
              />
            </SettingRow>
            <SettingRow label="Allow Business Accounts" description="Enable business account type">
              <Toggle
                enabled={settings.allow_business_accounts}
                onChange={(v) => updateSetting('allow_business_accounts', v)}
              />
            </SettingRow>
            <SettingRow label="Max Listings Per User" description="Maximum active listings per user">
              <input
                type="number"
                value={settings.max_listings_per_user}
                onChange={(e) => updateSetting('max_listings_per_user', parseInt(e.target.value) || 50)}
                min="1"
                max="500"
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
              />
            </SettingRow>
          </div>
        )}

        {activeTab === 'moderation' && (
          <div className="space-y-0">
            <SettingRow label="Auto-Moderate Images" description="Use AI to check uploaded images">
              <Toggle
                enabled={settings.auto_moderate_images}
                onChange={(v) => updateSetting('auto_moderate_images', v)}
              />
            </SettingRow>
            <SettingRow label="Profanity Filter" description="Filter inappropriate language">
              <Toggle
                enabled={settings.profanity_filter}
                onChange={(v) => updateSetting('profanity_filter', v)}
              />
            </SettingRow>
            <SettingRow label="Require Review Verification" description="Admin must approve reviews">
              <Toggle
                enabled={settings.require_review_verification}
                onChange={(v) => updateSetting('require_review_verification', v)}
              />
            </SettingRow>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="space-y-0">
            <SettingRow label="Email Notifications" description="Send email notifications to users">
              <Toggle
                enabled={settings.email_notifications}
                onChange={(v) => updateSetting('email_notifications', v)}
              />
            </SettingRow>
            <SettingRow label="Admin Email" description="Email for admin notifications">
              <input
                type="email"
                value={settings.admin_email}
                onChange={(e) => updateSetting('admin_email', e.target.value)}
                placeholder="admin@example.com"
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
              />
            </SettingRow>
          </div>
        )}

        {activeTab === 'social' && (
          <div className="space-y-0">
            <SettingRow label="Instagram URL" description="Your Instagram profile link">
              <input
                type="url"
                value={settings.instagram_url}
                onChange={(e) => updateSetting('instagram_url', e.target.value)}
                placeholder="https://instagram.com/youraccount"
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
              />
            </SettingRow>
            <SettingRow label="Facebook URL" description="Your Facebook page link">
              <input
                type="url"
                value={settings.facebook_url}
                onChange={(e) => updateSetting('facebook_url', e.target.value)}
                placeholder="https://facebook.com/yourpage"
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
              />
            </SettingRow>
          </div>
        )}
      </div>

      {/* Save Reminder */}
      <div className="flex items-center justify-end gap-4">
        <p className="text-sm text-muted-foreground">Remember to save your changes</p>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Save All
        </button>
      </div>
    </div>
  );
};

export default AdminSettings;
